import { FC, useEffect, useState } from "react";
import { Preloader, TopNav } from "../../components";
import { useGetEquipmentsQuery } from "../../store/equipmentsApi";
import pageComponents from "@/constants/";
import { tg } from "@/constants/telegram";
import { ActionButton } from "@/shared/ui";
import SparklesIcon from "@/assets/sparkles.png";
import { useRefreshSamplesMutation, useQuizFindAnswerMutation } from "@/store/usersApi";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { User } from "@/entities/user";
import { UserStatus } from "../TrainsPage/TrainsPage";
import { useYandexMetrika, GOALS } from "@/hooks/useYandexMetrika";
import { isQuizNotCompletedError, notifyQuizNotCompleted } from "@/shared/utils";
import { useAddAnswerMutation } from "@/store/quizApi";
import { AnswerType } from "@/store/quizApi";
import styles from "./EquipmentsPage.module.scss";
import { initTelegramWebApp } from "@/shared/utils/telegramInit";
import { useAppSelector } from "@/shared/hooks";
import KettlebellIcon from "@/assets/img/equipments/kettlebell.png";
import BodyWeightIcon from "@/assets/img/equipments/body-weight.jpeg";
import DumbbellIcon from "@/assets/img/equipments/dumbbell.png";
import BarbellIcon from "@/assets/img/equipments/barbell.png";
import RunningIcon from "@/assets/img/equipments/running.jpg";
import MachineIcon from "@/assets/img/equipments/exercise-machine.png";

type EquipmentsPageProps = {
  user: User;
};

export const EquipmentsPage: FC<EquipmentsPageProps> = (props) => {
  const { user } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const { trackButtonClick, trackAction } = useYandexMetrika();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const urlParams = new URLSearchParams(location.search);
  const adaptiveMode = urlParams.has("adaptiveMode");

  const [initData, setInitData] = useState<string | undefined>(undefined);

  useEffect(() => {
    try {
      // Инициализируем Telegram Web App с правильными настройками
      initTelegramWebApp();

      if (!initData && tg?.initData) {
        setInitData(tg.initData);
      }
    } catch (error) {
      console.error("Error in EquipmentsPage initialization:", error);
    }
  }, [initData]);

  const { data, isLoading } = useGetEquipmentsQuery({});
  const [refreshSamples] =
    useRefreshSamplesMutation();
  const [quizFindAnswer] = useQuizFindAnswerMutation();
  const [addAnswer] = useAddAnswerMutation();
  const [activePage, setActivePage] = useState(0);
  const [showDetailedSelection, setShowDetailedSelection] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const handleSetActivePage = (page: number) => {
    if (page === 0 && showDetailedSelection) {
      // Если возвращаемся на первую страницу из детального выбора, показываем простой выбор
      setShowDetailedSelection(false);
      setActivePage(0);
    } else if (page === 0 && !showDetailedSelection) {
      // Если мы на простом выборе и нажимаем назад, просто устанавливаем activePage
      setActivePage(0);
    } else {
      setActivePage(page);
    }
  };

  const equipmentOptions = [
    { name: "Гиря", description: "Любой подходящий тебе вес", image: KettlebellIcon },
    { name: "Собственный вес", description: "Без инвентаря", image: BodyWeightIcon },
    { name: "Гантеля", description: "Любой подходящий тебе вес", image: DumbbellIcon },
    { name: "Штанга", description: "Скамья, рама, различные диски для штанги", image: BarbellIcon },
    { name: "Бег", description: "Без инвентаря", image: RunningIcon },
    { name: "Тренажеры спортзала", description: "Все оборудование", image: MachineIcon },
  ];

  const handleOptionToggle = async (optionName: string) => {
    // Работаем и с initData, и с JWT авторизацией
    const hasAuth = initData || isAuthenticated || localStorage.getItem("access_token");
    if (!hasAuth) return;

    const newSelected = selectedOptions.includes(optionName)
      ? selectedOptions.filter((item) => item !== optionName)
      : [...selectedOptions, optionName];

    setSelectedOptions(newSelected);

    // Отправляем запрос с обновленным списком выбранных вариантов
    try {
      // Формируем строку с названиями и описаниями
      const answerText = newSelected
        .map((name) => {
          const option = equipmentOptions.find((opt) => opt.name === name);
          if (option && option.description) {
            return `${option.name} (${option.description})`;
          }
          return option?.name || name;
        })
        .join(", ");
      
      await addAnswer({
        question_id: 20,
        answer: answerText,
        answer_type: AnswerType.TEXT,
      }).unwrap();
    } catch (error) {
      console.error("Ошибка при отправке ответа:", error);
      // Не показываем ошибку пользователю, так как это может быть частым действием
    }
  };

  const handleDetailedSelection = () => {
    setShowDetailedSelection(true);
    setActivePage(1); // Устанавливаем activePage в 1, чтобы TopNav показывал кнопку назад
  };

  const handleBackToSimpleSelection = () => {
    setShowDetailedSelection(false);
  };

  const handleRefreshSamples = async (): Promise<boolean> => {
    // Работаем и с initData, и с JWT авторизацией
    const hasAuth = initData || isAuthenticated || localStorage.getItem("access_token");
    if (!hasAuth) return false;

    // Отслеживаем нажатие на кнопку "Адаптировать"
    trackButtonClick(GOALS.BUTTON_ADAPT, { adaptiveMode });

    try {
      // Сначала проверяем дополнительные вопросы
      const userData = await quizFindAnswer({ 
        data_type: ['additional_questions', 'gender'] 
      }).unwrap();

      if (userData.answers) {
        const additionalQuestions = userData.answers['additional_questions'];
        const gender = userData.answers['gender'];

        // Если пользователь уже прошел онбординг (select_samples === "Selected"), 
        // не редиректим на онбординг, а сразу запускаем адаптацию
        const isOnboardingCompleted = additionalQuestions === 'Не требуются';

        if (additionalQuestions === null || additionalQuestions === undefined) {
          // Если онбординг уже пройден, запускаем адаптацию и редиректим на /trains с поллингом
          if (isOnboardingCompleted) {
            trackAction(GOALS.ACTION_ADAPTATION_STARTED, { 
              hasAdditionalQuestions: false,
              gender 
            });
            
            try {
              const result = await refreshSamples({}).unwrap();
              if (result.task_id) {
                localStorage.setItem('adaptive_task_id', result.task_id);
                // Редиректим на /trains, где автоматически запустится поллинг
                navigate("/trains");
                toast.success("Адаптация запущена!");
                return true;
              } else {
                toast.error("Не удалось получить task_id");
                return false;
              }
            } catch (refreshError: any) {
              if (isQuizNotCompletedError(refreshError)) {
                notifyQuizNotCompleted();
                navigate("/quiz");
                return false;
              } else {
                // Если запрос не вернул успешный статус, показываем сообщение об ошибке
                toast.error("Произошла ошибка. Попробуйте через пару минут.");
                console.error("Ошибка при запуске адаптации:", refreshError);
                return false;
              }
            }
          } else {
            // Если онбординг не пройден, показываем онбординг
            trackAction(GOALS.ACTION_ADAPTATION_STARTED, { 
              hasAdditionalQuestions: false,
              gender 
            });
            
            refreshSamples({})
              .unwrap()
              .then((result) => {
                if (result.task_id) {
                  localStorage.setItem('adaptive_task_id', result.task_id);
                }
              })
              .catch((refreshError: any) => {
                if (isQuizNotCompletedError(refreshError)) {
                  notifyQuizNotCompleted();
                  navigate("/quiz");
                } else {
                  // Если запрос не вернул успешный статус, показываем сообщение об ошибке
                  toast.error("Произошла ошибка. Попробуйте через пару минут.");
                  console.error("Ошибка при запуске адаптации:", refreshError);
                }
              });
            const bodyFormPage = gender === 'Ж' ? '/girl-body-form' : '/body-form';
            navigate(bodyFormPage);
            toast.success("Адаптация запущена! Пока она идет, давайте заполним дополнительные вопросы.");
            return true;
          }
        } else {
          // Если дополнительные вопросы уже заполнены, запускаем адаптацию
          // Отслеживаем начало адаптации
          trackAction(GOALS.ACTION_ADAPTATION_STARTED, { 
            hasAdditionalQuestions: true,
            gender 
          });
          
          try {
            const result = await refreshSamples().unwrap();
            if (result.task_id) {
              localStorage.setItem('adaptive_task_id', result.task_id);
              // Редиректим на /trains, где автоматически запустится поллинг
              navigate("/trains");
              toast.success("Адаптация запущена!");
              return true;
            } else {
              toast.error("Не удалось получить task_id");
              return false;
            }
          } catch (refreshError: any) {
            if (isQuizNotCompletedError(refreshError)) {
              notifyQuizNotCompleted();
              navigate("/quiz");
              return false;
            } else {
              // Если запрос не вернул успешный статус, показываем сообщение об ошибке
              toast.error("Произошла ошибка. Попробуйте через пару минут.");
              console.error("Ошибка при запуске адаптации:", refreshError);
              return false;
            }
          }
        }
      } else {
        // Если пользователь уже прошел онбординг, не редиректим на онбординг
        const isOnboardingCompleted = user.select_samples === UserStatus.SELECTED;
        
        if (isOnboardingCompleted) {
          try {
            const result = await refreshSamples().unwrap();
            if (result.task_id) {
              localStorage.setItem('adaptive_task_id', result.task_id);
              // Редиректим на /trains, где автоматически запустится поллинг
              navigate("/trains");
              toast.success("Адаптация запущена!");
              return true;
            } else {
              toast.error("Не удалось получить task_id");
              return false;
            }
          } catch (refreshError: any) {
            if (isQuizNotCompletedError(refreshError)) {
              notifyQuizNotCompleted();
              navigate("/quiz");
              return false;
            } else {
              // Если запрос не вернул успешный статус, показываем сообщение об ошибке
              toast.error("Произошла ошибка. Попробуйте через пару минут.");
              console.error("Ошибка при запуске адаптации:", refreshError);
              return false;
            }
          }
        }
        
        refreshSamples()
          .unwrap()
          .then((result) => {
            if (result.task_id) {
              localStorage.setItem('adaptive_task_id', result.task_id);
            }
          })
          .catch((refreshError) => {
            if (isQuizNotCompletedError(refreshError)) {
              notifyQuizNotCompleted();
              navigate("/quiz");
            } else {
              console.error("Ошибка при запуске адаптации:", refreshError);
            }
          });
        navigate("/body-form");
        toast.success("Адаптация запущена! Пока она идет, давайте заполним дополнительные вопросы.");
        return true;
      }
    } catch (error) {
      if (isQuizNotCompletedError(error)) {
        notifyQuizNotCompleted();
        navigate("/quiz");
        return false;
      }

      console.error("Ошибка при проверке дополнительных вопросов:", error);
      
      // Если пользователь уже прошел онбординг, не редиректим на онбординг
      const isOnboardingCompleted = user.select_samples === UserStatus.SELECTED;
      
      if (isOnboardingCompleted) {
        try {
          const result = await refreshSamples().unwrap();
          if (result.task_id) {
            localStorage.setItem('adaptive_task_id', result.task_id);
            // Редиректим на /trains, где автоматически запустится поллинг
            navigate("/trains");
            toast.success("Адаптация запущена!");
            return true;
          } else {
            toast.error("Не удалось получить task_id");
            return false;
          }
        } catch (refreshError: any) {
          if (isQuizNotCompletedError(refreshError)) {
            notifyQuizNotCompleted();
            navigate("/quiz");
            return false;
          } else {
            // Если запрос не вернул успешный статус, показываем сообщение об ошибке
            toast.error("Произошла ошибка. Попробуйте через пару минут.");
            console.error("Ошибка при запуске адаптации:", refreshError);
            return false;
          }
        }
      }
      
      // В случае ошибки редиректим на страницу выбора формы только если онбординг не пройден
      refreshSamples()
        .unwrap()
        .then((result) => {
          if (result.task_id) {
            localStorage.setItem('adaptive_task_id', result.task_id);
          }
        })
        .catch((refreshError) => {
          if (isQuizNotCompletedError(refreshError)) {
            notifyQuizNotCompleted();
            navigate("/quiz");
          } else {
            console.error("Ошибка при запуске адаптации:", refreshError);
          }
        });
      navigate("/body-form");
      toast.success("Адаптация запущена! Пока она идет, давайте заполним дополнительные вопросы.");
      return true;
    }

    return true;
  };

  const handleSkipRefreshSamples = async () => {
    // Отслеживаем нажатие на кнопку "Пропустить"
    trackButtonClick(GOALS.BUTTON_SKIP_EQUIPMENT, { adaptiveMode });
    
    const isCompleted = await handleRefreshSamples();

    if (isCompleted) {
      toast.success("Выбор оборудования пропущен!");
    }
  };

  if (isLoading) return <Preloader />;

  const { equipments } = data;

  // Начальный экран с кнопками выбора
  if (!showDetailedSelection) {
    return (
      <div className="equipments-page">
        <div className="container">
          <TopNav
            activePage={activePage}
            linkPath={"/more"}
            handleSetActivePage={handleSetActivePage}
            backArrowNoAvailable={adaptiveMode}
            title="Настройки оборудования"
          />
          <div className={styles.simpleSelection}>
            <h2 className="main-title">Как ты хочешь тренироваться?</h2>
            <p className={styles.subtitle}>
              Выбери опции ниже в порядке предпочтения:
            </p>
            <div className={styles.optionsList}>
              {equipmentOptions.map((option) => (
                <button
                  key={option.name}
                  className={`${styles.optionButton} ${
                    selectedOptions.includes(option.name) ? styles.selected : ""
                  }`}
                  onClick={() => handleOptionToggle(option.name)}
                >
                  {option.image && (
                    <img 
                      src={option.image} 
                      alt={option.name}
                      className={styles.optionImage}
                    />
                  )}
                  <div className={styles.optionContent}>
                    <div className={styles.optionName}>{option.name}</div>
                    {option.description && (
                      <div className={styles.optionDescription}>
                        {option.description}
                      </div>
                    )}
                  </div>
                </button>
              ))}
              <button
                className={`${styles.optionButton} ${styles.detailedButton}`}
                onClick={handleDetailedSelection}
              >
                Детальный выбор оборудования
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "20px",
            position: "sticky",
            bottom: "0px",
            padding: "20px 0 10px",
            backgroundColor: "rgb(245 245 245 / 85%)",
            flexDirection: "column",
          }}
        >
          {adaptiveMode && (
            <ActionButton
              label="Пропустить"
              isBig
              isWhiteIcon
              onClick={handleSkipRefreshSamples}
              btnClassName="!bg-transparent !text-[--color-black] w-[173px] text-center flex items-center justify-center !border-[1px] !border-solid !border-[--color-black] transition-all active:bg-[#1bd730] hover:text-[--color-white] !py-[16px]"
            />
          )}
          <ActionButton
            label="Далее →"
            isBig
            onClick={handleRefreshSamples}
            btnClassName="!py-[16px]"
          />
        </div>
      </div>
    );
  }

  // Детальный выбор оборудования (существующий экран)
  return (
    <div className="equipments-page">
      <div className="container">
        <TopNav
          activePage={activePage}
          linkPath={"/more"}
          handleSetActivePage={handleSetActivePage}
          backArrowNoAvailable={adaptiveMode && !showDetailedSelection}
          title={
            activePage === 0
              ? "Настройки оборудования"
              : equipments[activePage - 1]?.title || "Неизвестная страница"
          }
        />
        {pageComponents(handleSetActivePage, equipments)[showDetailedSelection && activePage === 1 ? 0 : activePage]}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "20px",
          position: "sticky",
          bottom: "0px",
          padding: "20px 0 10px",
          backgroundColor: "rgb(245 245 245 / 85%)",
          flexDirection: "column",
        }}
      >
        {adaptiveMode && (
          <ActionButton
            label="Пропустить"
            isBig
            isWhiteIcon
            onClick={handleSkipRefreshSamples}
            btnClassName="!bg-transparent !text-[--color-black] w-[173px] text-center flex items-center justify-center !border-[1px] !border-solid !border-[--color-black] transition-all active:bg-[#1bd730] hover:text-[--color-white] !py-[16px]"
          />
        )}
        <ActionButton
          label="Адаптировать"
          icon={SparklesIcon}
          isBig
          onClick={handleRefreshSamples}
          btnClassName="!py-[16px]"
        />

        <span className="text-[12px] text-[--color-unimportant] text-center mt-3 !px-[16px] max-w-[300px]">
          *Выберите оборудование, которое у вас есть и в самом конце нажмите
          кнопку <span className="text-[#1bd730]">Адаптировать</span>.{" "}
          {adaptiveMode && "Либо вы можете пропустить этот этап"}
        </span>
      </div>
    </div>
  );
};
